import { getData, postData, putData, deleteData } from 'src/api/http.service';
import { getAPIEndpoints } from 'src/utils';

// Helper functions to recursively map the raw API tree into UI tree nodes.
const mapApiCategoryToUi = category => ({
  id: category.id,
  name: (category.name || '').trim(),
  type: 'CATEGORY',
  checked: category.active !== false,
  priority: category.priority,
  categoryDescription: (category.categoryDescription || '').trim(),
  children: (category.labels || []).map(mapApiLabelToUi),
});

const mapApiLabelToUi = label => ({
  id: label.id,
  name: (label.name || '').trim(),
  type: 'LABEL',
  checked: label.active !== false,
  priority: label.priority,
  children: (label.subCategories || []).map(mapApiSubCategoryToUi),
});

const mapApiSubCategoryToUi = subCategory => ({
  id: subCategory.id,
  name: (subCategory.name || '').trim(),
  type: 'SUBCATEGORY',
  checked: subCategory.active !== false,
  url: (subCategory.url || '').trim(),
  description: (subCategory.description || '').trim(),
  priority: subCategory.priority,
  children: [],
});

export const mapApiTreeToUiTree = apiTree => {
  if (!Array.isArray(apiTree)) return [];
  return apiTree.map(mapApiCategoryToUi);
};

// Helper functions to map UI tree category nodes back to the API payload shape.
export const mapUiCategoryToApi = category => ({
  id:
    category.id && !category.id.startsWith('category-') && !category.id.startsWith('cat-')
      ? category.id
      : undefined,
  name: category.name,
  priority: category.priority || 1,
  categoryDescription: category.categoryDescription ? category.categoryDescription.trim() : null,
  active: category.checked !== false,
  labels: (category.children || []).map((label, labelIndex) => ({
    id:
      label.id && !label.id.startsWith('label-') && !label.id.startsWith('lbl-')
        ? label.id
        : undefined,
    name: label.name,
    priority: labelIndex + 1,
    active: label.checked !== false,
    subCategories: (label.children || []).map((subCategory, subIndex) => ({
      id:
        subCategory.id &&
        !subCategory.id.startsWith('subcategory-') &&
        !subCategory.id.startsWith('sub-')
          ? subCategory.id
          : undefined,
      name: subCategory.name,
      url: subCategory.url || '',
      description: subCategory.description || '',
      priority: subIndex + 1,
      active: subCategory.checked !== false,
    })),
  })),
});

export function fetchNavigationTree() {
  return getData(getAPIEndpoints().getNavigationMenu).then(response => {
    const rawTree = response.data || [];
    const mappedTree = mapApiTreeToUiTree(rawTree);
    return {
      ...response,
      data: {
        data: mappedTree,
      },
    };
  });
}

export function saveNavigationTree(payload) {
  return putData(getAPIEndpoints().updateNavigationMenu, payload).then(response => {
    const rawData = response.data;
    const rawTree = Array.isArray(rawData) ? rawData : rawData?.data || [];
    const mappedTree = Array.isArray(rawTree) ? mapApiTreeToUiTree(rawTree) : [];
    return {
      ...response,
      data: {
        data: mappedTree,
      },
    };
  });
}

export function createCategory(categoryNode) {
  const payload = mapUiCategoryToApi(categoryNode);
  return postData(getAPIEndpoints().createCategory, payload);
}

export function updateCategory(categoryNode) {
  const payload = mapUiCategoryToApi(categoryNode);
  return putData(getAPIEndpoints().updateCategory, payload);
}

export function deleteCategory(categoryId) {
  const url = getAPIEndpoints().deleteNavigationCategory.replace('{categoryId}', categoryId);
  return deleteData(url);
}

export function deleteLabel(labelId) {
  const url = getAPIEndpoints().deleteLabel.replace('{labelId}', labelId);
  return deleteData(url);
}

export function deleteSubCategory(subCategoryId) {
  const url = getAPIEndpoints().deleteSubCategory.replace('{subCategoryId}', subCategoryId);
  return deleteData(url);
}

export function updateCategoryPriorities(prioritiesPayload) {
  return putData(getAPIEndpoints().updateCategoryPriority, prioritiesPayload);
}
